import React from 'react';
import PropTypes from 'prop-types';
import { Table, Header, Icon, Label } from 'semantic-ui-react';
import { formatCurrencyRupiah } from 'utils/currencyUtils';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';

const getModuleColor = module => {
  const colorMap = {
    Basic: 'blue',
    Professional: 'purple',
    Enterprise: 'orange',
  };
  return colorMap[module] || 'grey';
};

const APPROVAL_REQUIREMENT_LABELS = {
  HIGHER_DISCOUNT: 'Higher Discount',
  BONUS_SUBSCRIPTION: 'Bonus Subscription',
};

const ApprovalRequirements = ({ requirements }) => {
  if (!requirements || requirements.length === 0) {
    return <Label size="small">None</Label>;
  }
  return requirements.map(req => (
    <Label
      key={req}
      color="orange"
      size="small"
      style={{ marginBottom: '0.2em' }}
    >
      {APPROVAL_REQUIREMENT_LABELS[req] || req}
    </Label>
  ));
};

ApprovalRequirements.propTypes = {
  requirements: PropTypes.arrayOf(PropTypes.string),
};

ApprovalRequirements.defaultProps = {
  requirements: [],
};

const CustomPackageTable = ({
  packages,
  isLoading,
  onEdit,
  onDelete,
  showEligibleFrom,
  title,
  isUpgradeTable,
}) => {
  const upgradeColSpan = '4';
  const colSpan = showEligibleFrom ? '8' : '7';

  const renderUpgradeTableContent = () => {
    if (isLoading) {
      return (
        <Table.Row>
          <Table.Cell colSpan={upgradeColSpan}>
            <LoadingSpinner />
          </Table.Cell>
        </Table.Row>
      );
    }

    if (packages.length === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan={upgradeColSpan} style={{ textAlign: 'center' }}>
            No data
          </Table.Cell>
        </Table.Row>
      );
    }

    return packages.map(pkg => (
      <Table.Row key={pkg.id}>
        <Table.Cell
          style={{
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={pkg.name}
        >
          {pkg.name}
        </Table.Cell>
        <Table.Cell>{formatCurrencyRupiah(pkg.total_price)}</Table.Cell>
        <Table.Cell>{pkg.discount_rule.discount_percentage}%</Table.Cell>
        <Table.Cell textAlign="center">
          <Icon
            name="pencil"
            link
            color="grey"
            onClick={() => onEdit(pkg, false)}
            style={{ cursor: 'pointer' }}
          />
        </Table.Cell>
      </Table.Row>
    ));
  };

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <Table.Row>
          <Table.Cell colSpan={colSpan}>
            <LoadingSpinner />
          </Table.Cell>
        </Table.Row>
      );
    }

    if (packages.length === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan={colSpan} style={{ textAlign: 'center' }}>
            No data
          </Table.Cell>
        </Table.Row>
      );
    }

    return packages.map(pkg => (
      <Table.Row key={pkg.id}>
        <Table.Cell
          style={{
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={pkg.name}
        >
          {pkg.name}
        </Table.Cell>
        <Table.Cell>
          <Label color={getModuleColor(pkg.subscription_product.name)}>
            {pkg.subscription_product.name}
          </Label>
        </Table.Cell>
        <Table.Cell>{pkg.quantity} month(s)</Table.Cell>
        <Table.Cell>{pkg.discount_rule.discount_percentage}%</Table.Cell>
        <Table.Cell>{formatCurrencyRupiah(pkg.total_price)}</Table.Cell>
        {showEligibleFrom && (
          <Table.Cell>{pkg.eligible_from_subscription_product}</Table.Cell>
        )}
        <Table.Cell textAlign="center">
          <ApprovalRequirements requirements={pkg.approval_requirements} />
        </Table.Cell>
        <Table.Cell textAlign="center">
          <Icon
            name="pencil"
            link
            color="blue"
            onClick={() => onEdit(pkg, false)}
            style={{ marginRight: '1em', cursor: 'pointer' }}
          />
          <Icon
            name="trash"
            link
            color="red"
            onClick={() => onDelete(pkg, false)}
            style={{ cursor: 'pointer' }}
          />
        </Table.Cell>
      </Table.Row>
    ));
  };

  if (isUpgradeTable) {
    return (
      <div>
        <Header as="h3">{title}</Header>
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Upgrade Path</Table.HeaderCell>
              <Table.HeaderCell>Price Delta</Table.HeaderCell>
              <Table.HeaderCell>Discount Threshold</Table.HeaderCell>
              <Table.HeaderCell textAlign="center" width={2}>
                Action
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>{renderUpgradeTableContent()}</Table.Body>
        </Table>
      </div>
    );
  }

  return (
    <div>
      <Header as="h3">{title}</Header>
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Package Name</Table.HeaderCell>
            <Table.HeaderCell>Base Module</Table.HeaderCell>
            <Table.HeaderCell>Duration</Table.HeaderCell>
            <Table.HeaderCell>Discount</Table.HeaderCell>
            <Table.HeaderCell>Price</Table.HeaderCell>
            {showEligibleFrom && (
              <Table.HeaderCell>Eligible From</Table.HeaderCell>
            )}
            <Table.HeaderCell textAlign="center" width={2}>
              Approval
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="center" width={2}>
              Action
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>{renderTableContent()}</Table.Body>
      </Table>
    </div>
  );
};

CustomPackageTable.propTypes = {
  packages: PropTypes.array,
  isLoading: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  showEligibleFrom: PropTypes.bool,
  title: PropTypes.string,
  isUpgradeTable: PropTypes.bool,
};

CustomPackageTable.defaultProps = {
  packages: [],
  isLoading: false,
  showEligibleFrom: false,
  title: 'Customize Package',
  isUpgradeTable: false,
  onDelete: () => {},
};

export default CustomPackageTable;
